import { useEffect } from 'react';
import { left, type TaskEither } from 'fp-ts/lib/TaskEither';

import type { PreviewUploadsController } from '../../hooks/useDappFileHandlers';
import { type TypedError, typedError } from '../../lib/typedError';
import * as Form from '../Form';
import { DappPreviewPanel } from './DappPreviewPanel';
import { FormValidityObserver } from './components/FormValidityObserver';
import { FORM_ID, NewDappFormData } from './constants';
import { useNewDappPreviewData } from './hooks/useNewDappPreviewData';
import { usePackageNameAvailability } from './hooks/usePackageNameAvailability';
import { AdditionalInfoSection } from './sections/AdditionalInfoSection';
import { BasicDetailsSection } from './sections/BasicDetailsSection';
import { EditorsChoiceSection } from './sections/EditorsChoiceSection';
import { LegalComplianceSection } from './sections/LegalComplianceSection';
import type {
  FormState,
  NewDappDataType,
  NewDappInputChangeEvent,
  SingleFileFieldName,
} from './types';

export {
  MAX_DAPP_NAME_LENGTH,
  MAX_EDITORS_CHOICE_HEADLINE_LENGTH,
  MAX_EDITORS_CHOICE_TAG_LENGTH,
  MAX_SUBTITLE_LENGTH,
} from '../../constants/form';

interface NewDappFormProps {
  formData: FormState;
  previewUploads: PreviewUploadsController;
  handleInputChange: (event: NewDappInputChangeEvent) => void;
  handleSingleFileChange: (
    fieldName: SingleFileFieldName,
    file: File | null,
  ) => void;
  handleSubmit: (
    input: TaskEither<TypedError<'InvalidFormData'>, NewDappDataType>,
  ) => TaskEither<unknown, unknown>;
  onValidityChange?: (isValid: boolean) => void;
  onDraftValidityChange?: (isValid: boolean) => void;
  isBusy?: boolean;
}

export function NewDappForm({
  formData,
  previewUploads,
  handleInputChange,
  handleSingleFileChange,
  handleSubmit,
  onValidityChange,
  onDraftValidityChange,
  isBusy = false,
}: NewDappFormProps) {
  const previewImageCount = formData.dappPreviews.filter(
    (preview) => preview !== null,
  ).length;
  const previewData = useNewDappPreviewData(formData);
  const {
    packageNameError,
    isCheckingPackageName,
    showSpinner,
    packageNameValid,
  } = usePackageNameAvailability(formData.packageName);

  useEffect(() => {
    onDraftValidityChange?.(
      Boolean(formData.dappName.trim()) &&
        packageNameValid &&
        !isCheckingPackageName &&
        !packageNameError,
    );
  }, [
    formData.dappName,
    packageNameValid,
    isCheckingPackageName,
    packageNameError,
    onDraftValidityChange,
  ]);

  const wrappedHandleSubmit = (
    input: TaskEither<TypedError<'InvalidFormData'>, NewDappDataType>,
  ) => {
    if (packageNameError) {
      return left(typedError('InvalidFormData', packageNameError));
    }

    return handleSubmit(input);
  };

  return (
    <Form.Root
      id={FORM_ID}
      formDataShape={NewDappFormData}
      onSubmitData={wrappedHandleSubmit}
      className="flex flex-col gap-12 lg:flex-row lg:items-start lg:justify-between"
      validityKey={JSON.stringify(formData)}
    >
      <FormValidityObserver
        onValidityChange={onValidityChange}
        packageNameValid={packageNameValid}
        packageNameError={packageNameError}
        isCheckingPackageName={isCheckingPackageName}
        packageName={formData.packageName}
        hasDappIcon={!!formData.dappIcon}
        hasBanner={!!formData.banner}
        previewImageCount={previewImageCount}
        hasLanguages={formData.languages.length > 0}
      />

      <div
        inert={isBusy}
        aria-busy={isBusy}
        className="flex w-full max-w-[640px] flex-col gap-10"
      >
        <div className="flex flex-col gap-2">
          <div
            role="heading"
            aria-level={1}
            className="text-4xl font-medium leading-10 text-white"
          >
            Let&apos;s add your dApp details
          </div>
          <p className="text-sm text-seeker-400">
            This is the information users will see in the Solana Mobile dApp
            Store. You may upload a new release after this step.
          </p>
        </div>

        <BasicDetailsSection
          formData={formData}
          previewUploads={previewUploads}
          packageNameError={packageNameError}
          isCheckingPackageName={isCheckingPackageName}
          showPackageNameSpinner={showSpinner}
          packageNameValid={packageNameValid}
          onInputChange={handleInputChange}
          onSingleFileChange={handleSingleFileChange}
        />

        <AdditionalInfoSection
          formData={formData}
          onInputChange={handleInputChange}
        />

        <LegalComplianceSection
          formData={formData}
          onInputChange={handleInputChange}
        />

        <EditorsChoiceSection
          formData={formData}
          onInputChange={handleInputChange}
          onSingleFileChange={handleSingleFileChange}
        />

        <Form.Error className="mt-1 text-right" />
      </div>

      <div className="w-full shrink-0 lg:sticky lg:top-8 lg:w-[300px]">
        <DappPreviewPanel
          formData={previewData}
          previewDimensions={previewUploads.previewDimensions}
        />
      </div>
    </Form.Root>
  );
}

export type {
  FormState,
  NewDappDataType,
  NewDappInputChangeEvent,
  SingleFileFieldName,
};
